import React, { useContext, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import Title from "../components/header/Title";
import AppSnackbar from "../components/common/AppSnackbar";
import useSnackbar from "../hooks/useSnackbar";
import AuthContext from "../store/auth-context";
import NotificationService, { INotification, isNotificationRead } from "../services/NotificationService";
import UserService from "../services/UserService";
import IUser from "../entities/IUser";
import { unwrapList, getErrorMessage } from "../services/apiUtils";
import { getUserId } from "../services/userUtils";



const NOTIFICATION_TYPE = {
  ORDER: 1,
  PROMOTION: 2,
  ACTIVITY: 3,
  CHAT: 4,
} as const;

type SendType = "ORDER" | "PROMOTION" | "GENERAL";




const SEND_TYPES: Array<{ value: SendType; label: string; type: number; icon: React.ReactElement }> = [
  { value: "ORDER", label: "Order", type: NOTIFICATION_TYPE.ORDER, icon: <LocalShippingRoundedIcon fontSize="small" /> },
  { value: "PROMOTION", label: "Promotion", type: NOTIFICATION_TYPE.PROMOTION, icon: <LocalOfferRoundedIcon fontSize="small" /> },
  { value: "GENERAL", label: "General", type: NOTIFICATION_TYPE.ACTIVITY, icon: <NotificationsRoundedIcon fontSize="small" /> },
];


const TYPE_TABS: Array<{
  value: string;
  label: string;
  type: number;
  icon: React.ReactElement;
  color: string;
  bg: string;
}> = [
  { value: "CHATS", label: "Chats", type: NOTIFICATION_TYPE.CHAT, icon: <ChatBubbleOutlineRoundedIcon />, color: "#0F9D82", bg: "#DFF3EE" },
  { value: "ORDERS", label: "Orders", type: NOTIFICATION_TYPE.ORDER, icon: <LocalShippingRoundedIcon />, color: "#1976D2", bg: "#E3F0FD" },
  { value: "ACTIVITIES", label: "Activities", type: NOTIFICATION_TYPE.ACTIVITY, icon: <NotificationsRoundedIcon />, color: "#F5A623", bg: "#FDF0DE" },
  { value: "PROMOS", label: "Promos", type: NOTIFICATION_TYPE.PROMOTION, icon: <CampaignRoundedIcon />, color: "#E0457B", bg: "#FBE2EC" },
];

const notificationTypeMeta = (type?: number) =>
  TYPE_TABS.find((t) => t.type === Number(type));

const notificationTypeLabel = (type?: number) => {
  const meta = notificationTypeMeta(type);
  if (meta) return meta.label.replace(/s$/, "");
  return "-";
};

const emptyForm = {
  sendType: "ORDER" as SendType,
  title: "",
  message: "",
  imageUrl: "",
  uri: "",
  promotionStartDate: "",
  promotionEndDate: "",
  recipient: "ALL" as "ALL" | "SPECIFIC",
  specificUser: null as IUser | null,
};

const getUserDisplayName = (user?: IUser | null): string => {
  if (!user) return "";
  const fromParts = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return user.name || fromParts || user.username || user.email || user.emailId || "";
};

const getInitials = (name?: string | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const initials = parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[1][0]}`;
  return initials.toUpperCase();
};

const Notifications: React.FC = () => {
  const { user } = useContext(AuthContext);
  const { snackbar, showSuccess, showError, close } = useSnackbar();

  const [tab, setTab] = useState<string>("ORDERS");
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const load = () => {
    if (!user?.userId) return;
    setLoading(true);
    setError(null);
    // Fetch the user's notifications once and filter by notification type on
    // the client, so each tab (Chats/Orders/Activities/Promos) reliably shows
    // only its own type regardless of how the backend buckets them.
    NotificationService.getByUserAndType(user.userId, "ALL")
      .then((res: any) => {
        setNotifications(unwrapList<INotification>(res));
      })
      .catch((err: any) => {
        setNotifications([]);
        setError(getErrorMessage(err, "Notifications could not be loaded."));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const activeTabMeta = TYPE_TABS.find((t) => t.value === tab) || TYPE_TABS[1];

  const visibleNotifications = useMemo(
    () => notifications.filter((n) => Number(n.notificationType) === activeTabMeta.type),
    [notifications, activeTabMeta]
  );

  const loadUsers = () => {
    setUsersLoading(true);
    UserService.getUsers()
      .then((res: any) => setUsers(unwrapList<IUser>(res)))
      .catch((err: any) => {
        setUsers([]);
        showError(getErrorMessage(err, "Users could not be loaded."));
      })
      .finally(() => setUsersLoading(false));
  };

  const openUserPicker = () => {
    setUserSearch("");
    setUserPickerOpen(true);
    if (users.length === 0) loadUsers();
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = getUserDisplayName(u).toLowerCase();
      const email = (u.email || u.emailId || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, userSearch]);

  const resetForm = () => setForm(emptyForm);

  const handleClose = () => {
    setOpen(false);
    setSaveError(null);
    resetForm();
  };

  const selectedSendType = SEND_TYPES.find((t) => t.value === form.sendType) || SEND_TYPES[0];

  const handleCreate = () => {
    if (form.recipient === "SPECIFIC" && !form.specificUser) {
      setSaveError("Please select a user to send this notification to.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    NotificationService.create({
      title: form.title,
      message: form.message,
      notificationDate: null,
      status: 1,
      uri: form.uri || undefined,
      notificationType: selectedSendType.type,
      isRead: 0,
      imageUrl: form.imageUrl || undefined,
      promotionStartDate: form.sendType === "PROMOTION" ? form.promotionStartDate || null : null,
      promotionEndDate: form.sendType === "PROMOTION" ? form.promotionEndDate || null : null,
      userId: form.recipient === "SPECIFIC" ? getUserId(form.specificUser as any) : null,
    })
      .then(() => {
        showSuccess("Notification sent successfully.");
        handleClose();
        load();
      })
      .catch((err: any) => {
        setSaveError(getErrorMessage(err, "Could not send the notification."));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
        <Title title="Notifications" subtitle="View and send notifications to users" />
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshRoundedIcon />} onClick={load}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Send Notification
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "space-between", sm: "flex-start" },
          gap: { xs: 1, sm: 4 },
          mb: 2.5,
          mt: 1,
          px: 1,
        }}
      >
        {TYPE_TABS.map((t) => {
          const active = tab === t.value;
          return (
            <Box
              key={t.value}
              onClick={() => setTab(t.value)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                minWidth: 56,
              }}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: active ? t.color : t.bg,
                  color: active ? "#fff" : t.color,
                }}
              >
                {t.icon}
              </Avatar>
              <Typography
                variant="caption"
                sx={{ fontWeight: active ? 700 : 500, color: active ? t.color : "text.secondary" }}
              >
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          p: 2,
          minHeight: 400,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : visibleNotifications.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8, color: "grey.600" }}>
            No {activeTabMeta.label.toLowerCase()} to show.
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {visibleNotifications.map((n, idx) => {
              const unread = !isNotificationRead(n);
              const meta = notificationTypeMeta(n.notificationType) || activeTabMeta;
              return (
                <Box
                  key={n.notificationId ?? n.id ?? idx}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    bgcolor: unread ? "action.hover" : "transparent",
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={n.imageUrl || undefined}
                    sx={{ width: 44, height: 44, bgcolor: meta.bg, color: meta.color }}
                  >
                    {meta.icon || <NotificationsNoneRoundedIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body1" fontWeight={unread ? 700 : 500}>
                        {n.title || "Notification"}
                      </Typography>
                      <Chip size="small" label={notificationTypeLabel(n.notificationType)} />
                      {unread && <Chip size="small" color="error" label="Unread" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {n.message || n.body || n.description || "-"}
                    </Typography>
                    {(n.createdAt || n.notificationDate) && (
                      <Typography variant="caption" color="text.secondary">
                        {n.createdAt || n.notificationDate}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Send Notification dialog - mirrors the mobile app's Send Notification screen */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {saveError}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Notification Type
              </Typography>
              <Stack direction="row" spacing={1}>
                {SEND_TYPES.map((t) => {
                  const selected = form.sendType === t.value;
                  return (
                    <Box
                      key={t.value}
                      onClick={() => setForm((f) => ({ ...f, sendType: t.value }))}
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        py: 1.2,
                        px: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: selected ? "primary.light" : "secondary.light",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Box sx={{ color: selected ? "primary.main" : "secondary.main", display: "flex" }}>
                        {t.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={selected ? 700 : 500}
                        color={selected ? "text.primary" : "text.secondary"}
                        noWrap
                      >
                        {t.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <TextField
              fullWidth
              label="Title"
              placeholder="e.g. Order Created, Sale"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Message"
              placeholder="e.g. Order Number..., 10% off"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Image URL (optional)"
              placeholder="https://..."
              value={form.imageUrl}
              onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
            />

            {form.sendType === "PROMOTION" && (
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.promotionStartDate}
                  onChange={(e) => setForm((f) => ({ ...f, promotionStartDate: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.promotionEndDate}
                  onChange={(e) => setForm((f) => ({ ...f, promotionEndDate: e.target.value }))}
                />
              </Stack>
            )}

            <TextField
              fullWidth
              label="Deep Link URI (optional)"
              placeholder="e.g. /product/123"
              value={form.uri}
              onChange={(e) => setForm((f) => ({ ...f, uri: e.target.value }))}
            />

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Recipient
              </Typography>
              <Stack direction="row" spacing={1}>
                {(
                  [
                    { value: "ALL", label: "All Users", icon: <GroupRoundedIcon fontSize="small" /> },
                    { value: "SPECIFIC", label: "Specific User", icon: <PersonRoundedIcon fontSize="small" /> },
                  ] as const
                ).map((r) => {
                  const selected = form.recipient === r.value;
                  return (
                    <Box
                      key={r.value}
                      onClick={() => setForm((f) => ({ ...f, recipient: r.value }))}
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        py: 1.2,
                        px: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: selected ? "primary.light" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {selected ? (
                        <CheckCircleRoundedIcon fontSize="small" color="primary" />
                      ) : (
                        <Box sx={{ color: "text.secondary", display: "flex" }}>{r.icon}</Box>
                      )}
                      <Typography variant="body2" fontWeight={selected ? 700 : 500}>
                        {r.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              {form.recipient === "SPECIFIC" && (
                <Box
                  onClick={openUserPicker}
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.2,
                    cursor: "pointer",
                    bgcolor: "#fff",
                  }}
                >
                  {form.specificUser ? (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.light", color: "secondary.main", fontSize: 12 }}>
                      {getInitials(getUserDisplayName(form.specificUser))}
                    </Avatar>
                  ) : (
                    <PersonRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, color: form.specificUser ? "text.primary" : "text.secondary" }}
                    noWrap
                  >
                    {form.specificUser ? getUserDisplayName(form.specificUser) : "Tap to select a user"}
                  </Typography>
                  <ArrowDropDownRoundedIcon sx={{ color: "text.secondary" }} />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !form.title.trim() || !form.message.trim()}
          >
            {saving ? <CircularProgress size={20} /> : "Send Notification"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Select User dialog - mirrors the mobile app's "Select User" bottom sheet */}
      <Dialog open={userPickerOpen} onClose={() => setUserPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select User</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by Name..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end">
                    <TuneRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Divider />
          {usersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4, color: "grey.600" }}>
              No users found.
            </Box>
          ) : (
            <List sx={{ maxHeight: 360, overflowY: "auto" }}>
              {filteredUsers.map((u) => {
                const name = getUserDisplayName(u) || "Unnamed User";
                const email = u.email || u.emailId || "";
                return (
                  <ListItemButton
                    key={getUserId(u) || email}
                    onClick={() => {
                      setForm((f) => ({ ...f, specificUser: u }));
                      setUserPickerOpen(false);
                    }}
                    sx={{ borderRadius: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "secondary.light", color: "secondary.main" }}>
                        {getInitials(name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={name} secondary={email} />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <AppSnackbar snackbar={snackbar} onClose={close} />
    </Box>
  );
};

export default Notifications;
